<template>
  <div>
    <PartnerListWidget @click="onDialogOpen">
      <template #icon>
        <HgIcon icon="PiggyBank" size="large" theme="secondary" />
      </template>
      <template #content>
        <span>
          {{ t('integrations.ubsPreQualify.actionText') }}
          <span :class="$style.cta" :aria-label="$t('tipHelperText')">
            {{ t('integrations.ubsPreQualify.actionLink') }}
          </span>
        </span>
      </template>
    </PartnerListWidget>

    <UbsPreQualifyWidgetDialog
      v-if="isUbsPreQualifyWidgetDialogLoaded"
      :isModalVisible="isDialogOpen"
      :listingId="listing.id"
      @trackPartnerLink="trackPartnerLink"
      @dialogClose="onDialogClose"
    />
  </div>
</template>

<script lang="ts" setup>
import { HgIcon } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { defineAsyncComponent, ref } from 'vue';

import PartnerListWidget from '@/components/PartnerIntegrations/PartnerListWidget/PartnerListWidget.vue';
import {
  InteractionContainerDetail,
  InteractionContainerName,
  InteractionEvent,
  InteractionGoal,
} from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { useI18n } from 'vue-i18n';

defineOptions({
  name: 'UbsPreQualifyWidget',
});

const { t, locale } = useI18n();

interface Props {
  listing: Listing;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  trackPartnerLink: [
    link: string,
    goal: InteractionGoal,
    containerDetail: InteractionContainerDetail,
  ];
}>();

const UbsPreQualifyWidgetDialog = defineAsyncComponent(
  () => import('@/components/PartnerIntegrations/UbsPreQualify/UbsPreQualifyWidgetDialog.vue'),
);

const isDialogOpen = ref(false);
const isUbsPreQualifyWidgetDialogLoaded = ref(false);

const trackClickOnApplyForFinancingCertificate = (): void => {
  trackEventHarmonized(InteractionEvent.PARTNER_LINK, {
    h_event_category_006: InteractionEvent.PARTNER_LINK,
    h_event_action_007: InteractionGoal.OPEN_PRE_QUALIFY_UBS,
    h_event_label_008: InteractionContainerName.LINK,
  });
};

const trackPartnerLink = (
  externalWebsiteUrl: string,
  goal: InteractionGoal,
  containerDetail: InteractionContainerDetail,
): void => {
  emit('trackPartnerLink', externalWebsiteUrl, goal, containerDetail);
};

const onDialogClose = (): void => {
  isDialogOpen.value = false;
};

const onDialogOpen = (): void => {
  isDialogOpen.value = true;
  isUbsPreQualifyWidgetDialogLoaded.value = true;
  trackClickOnApplyForFinancingCertificate();
};
</script>
<style lang="scss" module>
@import '@/assets/variables';

.cta {
  color: var(--base-color-link);

  @include font-bold;
}
</style>
